import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Debounced auto-save.
 *
 * Watches `value` and calls `onSave` after `delay` ms of inactivity. The first value seen for a
 * given `key` is treated as the baseline (the initial load) and is NOT saved, only subsequent
 * edits trigger a save. Changing `key` (e.g. switching to a different flow) re-baselines without
 * saving. Saving is local-first and cheap, so this is safe to run on every edit.
 *
 * The hook owns the *outcome* as well as the scheduling: it awaits `onSave`, and exposes the last
 * failure as `error`. Auto-save is the user's protection against losing work, so a failure that is
 * only visible in the console is worse than no auto-save at all, the safety net stops working and
 * nothing says so. Callers are expected to render `error`.
 *
 * `onError` fires only on the *transition* into failure, not on every retry: auto-save re-runs on
 * each edit burst, and toasting each one would bury the message it is trying to deliver.
 *
 * @param {*} key Identity of the thing being edited (e.g. flow id); null/undefined disables saving
 * @param {*} value The value to watch for changes
 * @param {function} onSave Called (no args) when a debounced save should happen; may be async
 * @param {{delay?: number, enabled?: boolean, onError?: function}} options
 * @returns {{error: string|null, clearError: function}} the last failure, or null while healthy
 */
export function useAutoSave(key, value, onSave, { delay = 2500, enabled = true, onError } = {}) {
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const baselineKeyRef = useRef(null);
  const wasEnabledRef = useRef(false);
  const lastSeenRef = useRef(undefined);
  const timerRef = useRef(null);
  const [error, setError] = useState(null);
  // Mirrors `error` so the transition check does not depend on the state having flushed.
  const errorRef = useRef(null);

  const clearError = useCallback(() => {
    errorRef.current = null;
    setError(null);
  }, []);

  const runSave = useCallback(async () => {
    try {
      await onSaveRef.current();
      if (errorRef.current) {
        errorRef.current = null;
        setError(null);
      }
    } catch (err) {
      const message = err?.message || 'Auto-save failed';
      const firstFailure = !errorRef.current;
      errorRef.current = message;
      setError(message);
      // eslint-disable-next-line no-console
      console.warn('Auto-save failed:', err);
      if (firstFailure && onErrorRef.current) {
        onErrorRef.current(message);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled || key == null) {
      wasEnabledRef.current = false;
      return undefined;
    }
    // Don't save the initial value for a freshly-loaded key, only real edits after it.
    // Becoming enabled again is not an edit either: the builder that was hidden while the other
    // mode was on screen used to save the moment it came back, which re-uploaded an unchanged
    // flow to WhatsApp (and knocked a published one back to draft) on every mode switch. A value
    // that DID change while saving was paused (an edit made before the drift check resolved) is
    // still saved.
    const justEnabled = !wasEnabledRef.current;
    wasEnabledRef.current = true;
    const unchangedSincePaused = justEnabled && Object.is(value, lastSeenRef.current);
    lastSeenRef.current = value;
    if (baselineKeyRef.current !== key || unchangedSincePaused) {
      baselineKeyRef.current = key;
      return undefined;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => runSave(), delay);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [key, value, delay, enabled, runSave]);

  return { error, clearError };
}
